/**
 * Chips da instalação elétrica: situação e resultado da inspeção. Toda informação sai em palavra,
 * nunca só pela cor (issue #364).
 */
import React from 'react';
import { Chip, type SxProps, type Theme } from '@mui/material';
import {
  CheckCircle as OkIcon,
  ErrorOutline as BadIcon,
  HourglassEmpty as PendingIcon,
  PowerOff as OffIcon,
  WarningAmber as WarnIcon,
} from '@mui/icons-material';
import {
  ROTULO_DO_RESULTADO,
  ROTULO_DA_SITUACAO_DA_INSTALACAO,
  type ElectricalInstallationSituation,
  type ElectricalRecordResult,
} from '../../../types/electricalInstallation';

type ToneColor = 'info' | 'warning' | 'success' | 'error' | 'neutral';

/**
 * Texto e borda no tom escuro da paleta (o `main` do aviso e do sucesso fica abaixo de 4,5:1 sobre o
 * branco) e letra de 14px. No tema escuro volta o tom claro, que é o que contrasta com o fundo escuro.
 */
const readableChipSx =
  (color: ToneColor): SxProps<Theme> =>
  (theme) => {
    if (color === 'neutral') {
      return { fontSize: '0.875rem', fontWeight: 500, '& .MuiChip-icon': { color: 'inherit' } };
    }
    return {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: theme.palette[color].dark,
      borderColor: theme.palette[color].dark,
      '& .MuiChip-icon': { color: 'inherit' },
      ...theme.applyStyles('dark', {
        color: theme.palette[color].light,
        borderColor: theme.palette[color].light,
      }),
    };
  };

const SITUATION_CHIPS: Record<ElectricalInstallationSituation, { color: ToneColor; icon: React.ReactElement }> = {
  EM_CADASTRO: { color: 'info', icon: <PendingIcon /> },
  EM_DIA: { color: 'success', icon: <OkIcon /> },
  ATENCAO: { color: 'warning', icon: <WarnIcon /> },
  DESATIVADA: { color: 'neutral', icon: <OffIcon /> },
};

const RESULT_CHIPS: Record<ElectricalRecordResult, { color: ToneColor; icon: React.ReactElement }> = {
  CONFORME: { color: 'success', icon: <OkIcon /> },
  COM_RESSALVAS: { color: 'warning', icon: <WarnIcon /> },
  NAO_CONFORME: { color: 'error', icon: <BadIcon /> },
};

export const SituationChip: React.FC<{ situation: ElectricalInstallationSituation }> = ({ situation }) => {
  const { color, icon } = SITUATION_CHIPS[situation];
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={icon}
      label={ROTULO_DA_SITUACAO_DA_INSTALACAO[situation]}
      sx={readableChipSx(color)}
    />
  );
};

export const ResultChip: React.FC<{ result: ElectricalRecordResult }> = ({ result }) => {
  const { color, icon } = RESULT_CHIPS[result];
  return (
    <Chip size="small" variant="outlined" icon={icon} label={ROTULO_DO_RESULTADO[result]} sx={readableChipSx(color)} />
  );
};

/** Chip só de palavra, sem cor de aviso: "Sem inspeção", classe de tensão. */
export const PlainChip: React.FC<{ label: string }> = ({ label }) => (
  <Chip size="small" variant="outlined" label={label} sx={readableChipSx('neutral')} />
);

export const OverdueChip: React.FC<{ label: string }> = ({ label }) => (
  <Chip size="small" variant="outlined" icon={<BadIcon />} label={label} sx={readableChipSx('error')} />
);
